import { useNavigate } from "react-router-dom"
import { useListUsers } from "@src/presentation/hooks/users"
import { Skeleton } from "@heroui/react"
import { AlertCircle } from "lucide-react"
import { usePermission } from "@src/presentation/hooks/usePermission"
import { PermissionPlaceholder } from "@src/presentation/components/PermissionPlaceholder"

interface UsersSummaryCardProps {
    limit?: number
}

export function UsersSummaryCard({ limit = 5 }: UsersSummaryCardProps) {
    const navigate = useNavigate()
    const { hasPermission } = usePermission()
    const canViewUsers = hasPermission("user:read")

    // Early return if no permission
    if (!canViewUsers) {
        return <PermissionPlaceholder title="Recent Users" description="You need 'user:read' permission to view users." />
    }

    // Fetch users from API
    const { data: usersData, isLoading, error } = useListUsers({ page: 1, page_size: limit })

    const users = usersData?.users || []
    const totalUsers = usersData?.total || 0

    // Calculate counts based on user status
    const activeCount = users.filter((u) => u.is_active && !u.is_banned).length
    const inactiveCount = users.filter((u) => !u.is_active).length
    const bannedCount = users.filter((u) => u.is_banned).length

    const getStatusColor = (user: typeof users[0]) => {
        if (user.is_banned) {
            return "bg-destructive/10 text-destructive border-destructive/20"
        }
        if (!user.is_active) {
            return "bg-muted text-muted-foreground border-border"
        }
        return "bg-success/10 text-success border-success/20"
    }

    const getStatusLabel = (user: typeof users[0]) => {
        if (user.is_banned) return "Banned"
        if (!user.is_active) return "Inactive"
        return "Active"
    }

    const getInitials = (name: string) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2)
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="rounded border border-border bg-card shadow-none">
                {/* Header Skeleton */}
                <div className="border-b border-border p-6">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-lg" />
                            <div>
                                <Skeleton className="h-6 w-16 mb-1" />
                                <Skeleton className="h-4 w-32" />
                            </div>
                        </div>
                        <Skeleton className="h-8 w-24 rounded" />
                    </div>
                    {/* Quick Stats Skeleton */}
                    <div className="mt-4 grid grid-cols-3 gap-3">
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                        <Skeleton className="h-16 w-full rounded" />
                    </div>
                </div>
                {/* Recent Users List Skeleton */}
                <div className="divide-y divide-border">
                    {Array.from({ length: limit }).map((_, index) => (
                        <div key={index} className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Skeleton className="h-10 w-10 rounded" />
                                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-3 w-32" />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                                <Skeleton className="h-4 w-16" />
                                <Skeleton className="h-5 w-14 rounded" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    // Error state
    if (error) {
        return (
            <div className="rounded border border-border bg-secondary p-8">
                <div className="flex flex-col items-center justify-center text-center">
                    <AlertCircle className="h-12 w-12 text-danger mb-3" />
                    <p className="text-sm text-danger font-medium">Failed to load users</p>
                    <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
                </div>
            </div>
        )
    }

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            {/* Header with Stats */}
            <div className="border-b border-border p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Users</h2>
                            <p className="text-sm text-muted-foreground">System user accounts</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/users")}
                        className="rounded border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                    >
                        View All ({totalUsers})
                    </button>
                </div>

                {/* Quick Stats */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="rounded bg-success/5 p-3 border border-success/10">
                        <p className="text-xs text-muted-foreground">Active</p>
                        <p className="text-xl font-bold text-success">{activeCount}</p>
                    </div>
                    <div className="rounded bg-muted/30 p-3 border border-border">
                        <p className="text-xs text-muted-foreground">Inactive</p>
                        <p className="text-xl font-bold text-foreground">{inactiveCount}</p>
                    </div>
                    <div className="rounded bg-destructive/5 p-3 border border-destructive/10">
                        <p className="text-xs text-muted-foreground">Banned</p>
                        <p className="text-xl font-bold text-destructive">{bannedCount}</p>
                    </div>
                </div>
            </div>

            {/* Recent Users List */}
            <div className="divide-y divide-border">
                {users.map((user) => (
                    <div
                        key={user.user_id}
                        className="flex items-center justify-between p-4 transition-colors hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/users/${user.user_id}`)}
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex h-10 w-10 items-center justify-center rounded bg-primary/10 text-sm font-semibold text-primary">
                                {getInitials(user.display_name)}
                            </div>
                            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{user.display_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {user.roles.length > 0 ? user.roles[0].display_name : "No role"}
                            </span>
                            <span
                                className={`inline-flex items-center rounded border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${getStatusColor(user)}`}
                            >
                                {getStatusLabel(user)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {totalUsers === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <svg
                        className="h-12 w-12 text-muted-foreground/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                    </svg>
                    <p className="mt-2 text-sm text-muted-foreground">No users found</p>
                </div>
            )}
        </div>
    )
}
